import { listDocumentMeta } from '../docMeta';
import { getDocument, saveDocument } from '../library';
import { searchLibrary } from '../searchClient';
import { setTaskChecked } from '../tasks';
import { updateFrontmatter } from '@markbird/shared';
import { t } from '../../i18n';
import { isBuiltinField } from './parse';
import { renderQueries, type QueryLabels } from './render';
import type { QueryDeps } from './run';

export { isBuiltinField, parseQueryBlock } from './parse';
export { runQuery } from './run';
export { renderQueries } from './render';

/** The library, as a query block sees it. */
const deps: QueryDeps = {
  listDocs: () => listDocumentMeta(),
  search: async (query, limit) =>
    (await searchLibrary(query, limit)).map((hit) => ({ id: hit.id, score: hit.score })),
  getContent: async (id) => (await getDocument(id))?.content,
  async setTaskChecked(docId, line, checked, text) {
    const doc = await getDocument(docId);
    if (!doc) return;
    // The text it saw goes with it: a note that changed in the meantime keeps
    // its tasks rather than having the wrong one ticked.
    const next = setTaskChecked(doc.content, line, checked, text);
    if (next) await saveDocument(docId, { content: next });
  },
  async setField(docId, field, value) {
    const doc = await getDocument(docId);
    if (!doc) return;
    // An emptied cell removes the property rather than leaving `field: ""`.
    const next = updateFrontmatter(doc.content, { [field]: value.trim() || undefined });
    if (next !== doc.content) await saveDocument(docId, { content: next });
  },
};

function labels(): QueryLabels {
  return {
    empty: t('query.empty'),
    emptyTasks: t('query.emptyTasks'),
    count: (n) => t('query.count', { n }),
    countTasks: (n) => t('query.countTasks', { n }),
    toggleTask: (text) => t('query.toggleTask', { text }),
    problems: (what) => t('query.problems', { what }),
    // A built-in column has a translated name; a property is called what the
    // person called it.
    field: (field) => (isBuiltinField(field) ? t(`query.field.${field}`) : field),
    sortBy: (field) => t('query.sortBy', { field }),
    editField: (field, title) => t('query.editField', { field, title }),
    untitled: t('common.untitled'),
  };
}

/** Fill the query blocks in a rendered preview. */
export function renderQueriesInDom(root: ParentNode, currentDocId?: string): Promise<void> {
  return renderQueries(root, deps, labels(), currentDocId);
}
